import type { ApiError } from "./client"

export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: "Invalid email address",
}

export const namePattern = {
  value: /^[A-Za-z\s\u00C0-\u017F]{1,30}$/,
  message: "Invalid name",
}

export const passwordRules = (isRequired = true) => {
  const rules: any = {
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters",
    },
  }

  if (isRequired) {
    rules.required = "Password is required"
  }

  return rules
}

export const confirmPasswordRules = (
  getValues: () => any,
  isRequired = true,
) => {
  const rules: any = {
    validate: (value: string) => {
      const password = getValues().password || getValues().newPassword
      return value === password ? true : "The passwords do not match"
    },
  }

  if (isRequired) {
    rules.required = "Password confirmation is required"
  }

  return rules
}

export const handleError = (err: ApiError, showToast: any) => {
  const errDetail = (err.body as any)?.detail
  let errorMessage = errDetail || "Something went wrong."
  if (Array.isArray(errDetail) && errDetail.length > 0) {
    errorMessage = errDetail[0].msg
  }
  showToast("Error", errorMessage, "error")
}

// Universal search helper that can be reused across components
// - items: array of data to search
// - query: search string; empty or whitespace-only query returns original items
// - options:
//    - keys: object keys to search in; if omitted, item will be stringified
//    - caseSensitive: controls case sensitivity (default false)
//    - trim: trims query before search (default true)
//    - stringify: custom stringify function for items when keys not provided
export function universalSearch<T extends Record<string, any>>(
  items: T[],
  query: string | undefined | null,
  options?: {
    keys?: (keyof T)[]
    caseSensitive?: boolean
    trim?: boolean
    stringify?: (item: T) => string
  },
): T[] {
  if (!Array.isArray(items) || items.length === 0) return items

  const caseSensitive = options?.caseSensitive ?? false
  const trim = options?.trim ?? true
  let q = query ?? ""
  if (trim) q = q.trim()
  if (!caseSensitive) q = q.toLowerCase()
  if (q.length === 0) return items

  const keys = options?.keys
  const stringify: (item: T) => string =
    options?.stringify ??
    ((item: T) => {
      if (keys && keys.length > 0) {
        const values = keys
          .map((k) => item?.[k])
          .filter((v) => v !== undefined && v !== null)
        return values.join(" ")
      }
      // fallback: join all primitive values of the object
      return Object.values(item)
        .filter((v) => typeof v !== "object")
        .join(" ")
    })

  return items.filter((item) => {
    let hay = stringify(item)
    if (!caseSensitive) hay = hay.toLowerCase()
    return hay.includes(q)
  })
}
